/**
 * Hand-drawn signature (mouse / touch) on a canvas. Reports the PNG data URL
 * on every stroke ('' after "נקה חתימה"). Used by the advisory agreement and
 * the מאזן service agreement.
 */

import { useRef, useEffect, useCallback } from 'react'

export default function SignaturePad({
  onSignatureChange,
  isEmpty,
  setIsEmpty,
  ariaLabel = 'תיבת חתימה בכתב יד (לא חובה — ניתן להזין שם מלא כחלופה)',
  describedBy,
}: {
  onSignatureChange: (dataUrl: string) => void
  isEmpty: boolean
  setIsEmpty: (v: boolean) => void
  ariaLabel?: string
  describedBy?: string
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const lastPos = useRef<{ x: number; y: number } | null>(null)

  const getPos = (e: { clientX: number; clientY: number }, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }

  const startDraw = useCallback((e: MouseEvent | TouchEvent) => {
    e.preventDefault()
    const canvas = canvasRef.current!
    drawing.current = true
    const point = e instanceof TouchEvent
      ? getPos(e.touches[0], canvas)
      : getPos(e as MouseEvent, canvas)
    lastPos.current = point
  }, [])

  const draw = useCallback((e: MouseEvent | TouchEvent) => {
    e.preventDefault()
    if (!drawing.current) return
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    const point = e instanceof TouchEvent
      ? getPos(e.touches[0], canvas)
      : getPos(e as MouseEvent, canvas)

    ctx.beginPath()
    ctx.moveTo(lastPos.current!.x, lastPos.current!.y)
    ctx.lineTo(point.x, point.y)
    ctx.strokeStyle = '#1e3a5f'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()

    lastPos.current = point
    setIsEmpty(false)
    onSignatureChange(canvas.toDataURL('image/png'))
  }, [onSignatureChange, setIsEmpty])

  const endDraw = useCallback(() => {
    drawing.current = false
    lastPos.current = null
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current!
    canvas.addEventListener('mousedown', startDraw)
    canvas.addEventListener('mousemove', draw)
    canvas.addEventListener('mouseup', endDraw)
    canvas.addEventListener('mouseleave', endDraw)
    canvas.addEventListener('touchstart', startDraw, { passive: false })
    canvas.addEventListener('touchmove', draw, { passive: false })
    canvas.addEventListener('touchend', endDraw)
    return () => {
      canvas.removeEventListener('mousedown', startDraw)
      canvas.removeEventListener('mousemove', draw)
      canvas.removeEventListener('mouseup', endDraw)
      canvas.removeEventListener('mouseleave', endDraw)
      canvas.removeEventListener('touchstart', startDraw)
      canvas.removeEventListener('touchmove', draw)
      canvas.removeEventListener('touchend', endDraw)
    }
  }, [startDraw, draw, endDraw])

  const clear = () => {
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setIsEmpty(true)
    onSignatureChange('')
  }

  return (
    <div>
      <div className="relative border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 overflow-hidden"
           style={{ touchAction: 'none' }}>
        <canvas
          ref={canvasRef}
          width={700}
          height={160}
          className="w-full block cursor-crosshair"
          style={{ height: '160px' }}
          role="img"
          aria-label={ariaLabel}
          aria-describedby={describedBy}
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
            <p className="text-gray-400 text-sm">חתום/י כאן עם העכבר או האצבע</p>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={clear}
        className="mt-2 text-sm text-gray-500 hover:text-red-500 underline transition-colors"
      >
        נקה חתימה
      </button>
    </div>
  )
}
